import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Forbidden } from '@vyaguta/vyaguta-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useTitle from 'hooks/useTitle';
import useEmployeeDebounceOptions from 'hooks/useEmployeeDebounceOptions';
import { useMyRoleQuery } from 'hooks/query/useMyRole';

import {
  createLeaveIssuerChangeRequest,
  updateLeaveIssuerChangeRequest,
  fetchLeaveIssuerChangeRequestByUserId,
} from 'services/leaveIssuerChangeRequest';
import * as userService from 'services/user';
import * as mixpanel from 'services/mixPanel';
import * as attendanceService from 'services/attendance';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { intersect } from 'utils/array';
import { isObjectEmpty } from 'utils/object';
import { getFormErrorText } from 'utils/form';
import { handleError } from 'utils/errorHandler';
import { extractFullName, getFirstName, interpolate } from 'utils/string';
import { getLeaveIssueCountMessage } from 'utils/issueeCountMessage';
import { addMonths, getFirstDayOfMonth, getFormattedDate, isDateInPast, isSameDate } from 'utils/date';

import { PendingActionsInfo } from 'components/common/issuerChange';
import Loading from 'components/common/loading/Loading';
import { LeaveIssuersHistory } from 'components/common/issuerChange';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';
import Info from 'components/common/inputWrapper/components/Info';
import ProfileHeader from '../components/profileHeader/ProfileHeader';
import Input from 'components/common/inputWrapper/components/Input';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import FormRadioGroup from 'components/common/inputWrapper/components/RadioGroup';
import DebounceFormDropdown from 'components/common/debounceFormDropdown/DebounceFormDropdown';

import { changeIssuerSchema } from 'schemas/changeIssuerSchema';

import {
  ROLE,
  ROLES_IDS,
  LIST_TYPE,
  USER_FETCH_FIELDS,
  NEW_LEAVE_ISSUER_IS_OPTIONS,
  REACT_DATEPICKER_DATE_FORMAT,
} from 'constants/appConstants';
import { SEMI_ANNUAL } from 'constants/date';
import { mixPanelEvents } from 'constants/mixPanel';
import { EMPLOYEE_PROFILE, HOME } from 'constants/routes';
import { ALL, ROLE_TYPES, TEAM } from 'constants/fetchTypes';
import { LEAVE_ISSUER_CHANGE_STATUS } from 'constants/changeLeaveIssuer';
import { DEFAULT_ISSUE_ALERT_COUNT, USER_TEAM_COUNT_TYPE } from 'constants/leave';
import type { ChangeIssuer, LeaveIssuerHistoryItem } from 'interface/issuer';
import type { EmployeeLike } from 'interface/userUtils';

const newLeaveIssuerRoleOptions = [ROLE.PROJECT_MANAGER, ROLE.TEAM_LEAD, ROLE.TEAM_MANAGER, ROLE.TEAM_MEMBER];

const getNewLeaveIssuerRole = (newLeaveIssuerRole) => {
  if (newLeaveIssuerRoleOptions.includes(newLeaveIssuerRole)) {
    return newLeaveIssuerRole;
  }

  return ROLE.OTHER;
};

const getOtherRole = (newLeaveIssuerRole) => {
  if (newLeaveIssuerRoleOptions.includes(newLeaveIssuerRole)) {
    return '';
  }

  return newLeaveIssuerRole;
};

interface ChangeIssuerRequestProps extends RouteComponentProps<{ id?: string }> {
  user: EmployeeLike;
  match: { params: { id?: string } };
  location?: {
    state?: {
      employee?: EmployeeLike;
      leaveIssuerRequest?: ChangeIssuer & {
        user?: { id: number };
        newLeaveIssuer?: { id: number; fullname: string };
      };
    };
  };
}

type HeaderEmployee = React.ComponentProps<typeof ProfileHeader>['employee'];
type ChangeIssuerEmployee = HeaderEmployee & {
  leaveIssuer?: { id?: number };
  teamManager?: { id?: number };
};

interface LeaveIssuerHistoryWithProfile extends LeaveIssuerHistoryItem {
  id: number;
}

interface ChangeIssuerFormValues {
  reason: string;
  transitionDate: moment.Moment;
  newLeaveIssuerId: string | number;
  newLeaveIssuerRole: string;
  otherRole: string;
}

const ChangeIssuerRequest: React.FC<ChangeIssuerRequestProps> = (props) => {
  useTitle('Change Leave Issuer Request');

  const { user } = props;

  const id = props.match.params.id;
  const currentUserId = props.user?.id;
  const authUserId = typeof currentUserId === 'number' ? currentUserId : Number(currentUserId);

  const [leaveIssueCount, setleaveIssueCount] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [havePermission, setHavePermission] = useState(true);
  const [isEmployeeLoading, setIsEmployeeLoading] = useState<boolean>(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [leaveIssuerHistory, setLeaveIssuerHistory] = useState<LeaveIssuerHistoryItem[]>();
  const [isIssueeCountLoading, setIsIssueeCountLoading] = useState(false);
  const [loadedLeaveIssuerId, setLoadedLeaveIssuerId] = useState<string | number | null>(null);
  const [employee, setEmployee] = useState<ChangeIssuerEmployee | undefined>(
    props.location?.state?.employee as ChangeIssuerEmployee | undefined
  );
  const [isLeaveIssuerRequestLoading, setIsLeaveIssuerRequestLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState({ leave: 0, worklog: 0 });
  const [leaveIssuerRequest, setLeaveIssuerRequest] = useState<
    | (ChangeIssuer & {
        user?: { id: number };
        newLeaveIssuer?: { id: number; fullname: string };
      })
    | undefined
  >(props.location?.state?.leaveIssuerRequest);

  const INITIAL_VALUES = {
    reason: leaveIssuerRequest?.reason || '',
    transitionDate: moment(leaveIssuerRequest?.transitionDate),
    newLeaveIssuerId: leaveIssuerRequest?.newLeaveIssuer?.id || '',
    newLeaveIssuerRole: leaveIssuerRequest?.newLeaveIssuerRole
      ? getNewLeaveIssuerRole(leaveIssuerRequest.newLeaveIssuerRole)
      : '',
    otherRole: leaveIssuerRequest?.newLeaveIssuerRole ? getOtherRole(leaveIssuerRequest.newLeaveIssuerRole) : '',
  };
  const [formValues, setFormValues] = useState(INITIAL_VALUES);

  const selectedNewLeaveIssuerId = formValues.newLeaveIssuerId || leaveIssuerRequest?.newLeaveIssuer?.id;

  const employeeDebounceOptions = useEmployeeDebounceOptions(selectedNewLeaveIssuerId, {
    roleIds: ROLES_IDS.ALL,
    fields: USER_FETCH_FIELDS.ROLES,
  });
  const selectedNewLeaveIssuerOption = useMemo(
    () => employeeDebounceOptions[1].find((option) => option.value.toString() === selectedNewLeaveIssuerId?.toString()),
    [employeeDebounceOptions, selectedNewLeaveIssuerId]
  );
  const selectedLeaveIssuerName = useMemo(
    () => getFirstName(String(selectedNewLeaveIssuerOption?.label || '')),
    [selectedNewLeaveIssuerOption]
  );
  const existingRequestLeaveIssuerId = leaveIssuerRequest?.newLeaveIssuer?.id;

  const isEmployeeEmpty = useMemo(() => isObjectEmpty(employee), [employee]);
  const isLeaveIssuerRequestEmpty = useMemo(() => isObjectEmpty(leaveIssuerRequest), [leaveIssuerRequest]);

  const { data: userRoles = [] } = useMyRoleQuery(Number.isFinite(authUserId) ? authUserId : undefined);

  const canRequestLeaveIssuer =
    (!pendingRequests.leave && !pendingRequests.worklog) ||
    !!intersect(userRoles, [ROLE.SUPER_ADMIN, ROLE.HR_ADMIN]).length;

  const isLeaveIssuer = employee?.leaveIssuer?.id === currentUserId;
  const isTeamManager = employee?.teamManager?.id === currentUserId;

  useEffect(() => {
    if (!selectedNewLeaveIssuerId) {
      return;
    }

    const controller = new AbortController();

    const fetchIssueeCount = async () => {
      setIsIssueeCountLoading(true);

      try {
        const { count } = (await userService.fetchIssueeCount(selectedNewLeaveIssuerId, {
          count: USER_TEAM_COUNT_TYPE.LEAVE_ISSUE,
        })) as { count: { leaveIssuee: number } };

        if (!controller.signal.aborted) {
          setleaveIssueCount(count?.leaveIssuee || 0);
          setLoadedLeaveIssuerId(selectedNewLeaveIssuerId);
          setIsIssueeCountLoading(false);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          handleError(e);
          setIsIssueeCountLoading(false);
        }
      }
    };

    void fetchIssueeCount();

    return () => controller.abort();
  }, [selectedNewLeaveIssuerId]);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) {
      return;
    }
    hasMountedRef.current = true;
    const { form: viewChangeRequestForm } = mixPanelEvents.view.leaveIssuerChangeRequest;

    if (!employee || isEmployeeEmpty) {
      void (async () => {
        setIsEmployeeLoading(true);

        try {
          const data = (await userService.fetchById(id)) as ChangeIssuerEmployee;

          setEmployee(data);
        } catch (error) {
          handleError(error);
        } finally {
          setIsEmployeeLoading(false);
        }
      })();
    }

    if (!leaveIssuerRequest || isLeaveIssuerRequestEmpty) {
      void (async () => {
        try {
          setIsLeaveIssuerRequestLoading(true);

          const { data } = (await fetchLeaveIssuerChangeRequestByUserId(id)) as {
            data: ChangeIssuer & {
              user?: { id: number };
              newLeaveIssuer?: { id: number; fullname: string };
            };
          };

          setHavePermission(true);
          setLeaveIssuerRequest(data);
        } catch {
          setHavePermission(false);
        } finally {
          setIsLeaveIssuerRequestLoading(false);
        }
      })();
    }

    mixpanel.trackEvent(viewChangeRequestForm);

    void (async () => {
      try {
        const data = (await userService.fetchLeaveIssuerHistory(props.match.params.id)) as LeaveIssuerHistoryItem[];

        setLeaveIssuerHistory(data);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingHistory(false);
      }
    })();

    void (async () => {
      try {
        const params = {
          fetchType: isLeaveIssuer || isTeamManager ? TEAM : ALL,
          userId: id,
          fields: 'worklog,leave',
          startDate: getFirstDayOfMonth(1),
          endDate: addMonths(getFormattedDate(), SEMI_ANNUAL),
          roleType: isTeamManager ? ROLE_TYPES.MANAGER : undefined,
        };

        const { data } = (await attendanceService.fetchSummary(params)) as {
          data: { leave?: { requested?: number }; worklog?: { submitted?: number } };
        };

        setPendingRequests({
          leave: data?.leave?.requested || 0,
          worklog: data?.worklog?.submitted || 0,
        });
      } catch (error) {
        handleError(error);
      }
    })();
  }, [
    employee,
    id,
    isEmployeeEmpty,
    isLeaveIssuerRequestEmpty,
    isLeaveIssuer,
    isTeamManager,
    leaveIssuerRequest,
    props.match.params.id,
  ]);

  const latestTransitionDate = useMemo(() => leaveIssuerHistory?.[0]?.transitionDate, [leaveIssuerHistory]);

  const createUpdateIssuerChangeRequest = async (data: Omit<ChangeIssuer, 'otherRole'> | { status: string }) => {
    try {
      setIsSubmitting(true);

      const leaveIssuerChangeRequestEvent = isLeaveIssuerRequestEmpty
        ? mixPanelEvents.send.leaveIssuerChangeRequest
        : mixPanelEvents.approve.leaveIssuerChangeRequest.byRecipient;

      if (isLeaveIssuerRequestEmpty) {
        await createLeaveIssuerChangeRequest(id, data as ChangeIssuer);
      } else {
        await updateLeaveIssuerChangeRequest(leaveIssuerRequest?.user?.id, data);
      }

      toast.success({
        title: 'Success',
        message: `Leave issuer change request has been ${
          isLeaveIssuerRequestEmpty ? 'created' : 'approved'
        } successfully.`,
      });

      mixpanel.trackEvent(leaveIssuerChangeRequestEvent);

      history.push(interpolate(EMPLOYEE_PROFILE, { id }));
    } catch (e) {
      handleError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (values: ChangeIssuerFormValues) => {
    const data = isLeaveIssuerRequestEmpty
      ? {
          reason: values.reason,
          transitionDate: getFormattedDate(values.transitionDate),
          newLeaveIssuerId: Number(values.newLeaveIssuerId),
          newLeaveIssuerRole: values.newLeaveIssuerRole === ROLE.OTHER ? values.otherRole : values.newLeaveIssuerRole,
        }
      : { status: LEAVE_ISSUER_CHANGE_STATUS.APPROVED };

    const updatedData = _.omit(data, 'otherRole');

    createUpdateIssuerChangeRequest(updatedData);
  };

  const isSelfNewIssuer = leaveIssuerRequest?.newLeaveIssuer?.id === user.id;
  const currentLeaveIssuer = (leaveIssuerHistory as LeaveIssuerHistoryWithProfile[] | undefined)?.[
    leaveIssuerHistory?.findIndex(
      ({ transitionDate }) => isDateInPast(transitionDate) || isSameDate(transitionDate, getFormattedDate())
    )
  ];
  const hasLoadedSelectedLeaveIssuerCount = useMemo(
    () =>
      Boolean(selectedNewLeaveIssuerId) &&
      loadedLeaveIssuerId !== null &&
      String(loadedLeaveIssuerId) === String(selectedNewLeaveIssuerId) &&
      leaveIssueCount !== null,
    [leaveIssueCount, loadedLeaveIssuerId, selectedNewLeaveIssuerId]
  );
  const canShowNewRequestLeaveIssuerInfo =
    Boolean(selectedNewLeaveIssuerId) &&
    isLeaveIssuerRequestEmpty &&
    !isIssueeCountLoading &&
    hasLoadedSelectedLeaveIssuerCount &&
    Boolean(selectedNewLeaveIssuerOption);
  const canShowExistingRequestLeaveIssuerInfo =
    Boolean(existingRequestLeaveIssuerId) &&
    !isLeaveIssuerRequestEmpty &&
    Boolean(currentLeaveIssuer) &&
    !isIssueeCountLoading &&
    hasLoadedSelectedLeaveIssuerCount;

  const isRequestedBySelf = user.id === +id;

  if (!havePermission || isRequestedBySelf) {
    return (
      <Forbidden
        className="mbpx-20"
        redirectHandler={() => history.push(HOME)}
        title="Forbidden"
        message="You do not have permission."
        image=""
        btnMessage="Go back"
      />
    );
  }

  if (isEmployeeLoading || !employee || isLeaveIssuerRequestLoading || isLoadingHistory) {
    return (
      <div className="loading-container container">
        <Loading />
      </div>
    );
  }

  return (
    <main>
      <div className="title profile-wrapper">
        <ProfileHeader employee={employee} user={{ id: Number(user.id) }} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap leaveissuer-page-container">
        <div className="full-scope-card momr-20 leaveissuer-form-container">
          <div className="change-issuer-request__title">
            <span>Request to Change Leave Issuer</span>
          </div>
          <>
            {!isLeaveIssuerRequestEmpty && currentLeaveIssuer && (
              <div className="d-flex gap-px-25 change-issuer-request__top">
                <div className="top-label">Current Leave Issuer</div>
                <Link
                  to={interpolate(EMPLOYEE_PROFILE, { id: currentLeaveIssuer.id })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {extractFullName(currentLeaveIssuer)}
                </Link>
              </div>
            )}
            <Formik
              initialValues={INITIAL_VALUES}
              validationSchema={changeIssuerSchema}
              onSubmit={(values: ChangeIssuerFormValues) => {
                handleSubmit(values);
              }}
            >
              {(props) => {
                const handleChange = (event) => {
                  const { name, value } = event.target;

                  if (name === 'newLeaveIssuerId') {
                    setleaveIssueCount(null);
                    setLoadedLeaveIssuerId(null);
                    setIsIssueeCountLoading(Boolean(value));
                  }

                  setFormValues((prevValues) => ({
                    ...prevValues,
                    [name]: value,
                  }));

                  return props.handleChange(event);
                };

                return (
                  <Form>
                    <div className="full-scope-card__content">
                      <div className="d-flex change-issuer-request__wrap">
                        <PendingActionsInfo
                          pendingLeaves={pendingRequests.leave}
                          pendingWorklogs={pendingRequests.worklog}
                          user={extractFullName(employee)}
                          isLeaveIssuer={isLeaveIssuer}
                        />
                        <div className="form-wrap__row form-wrap__row--no-margin width--full">
                          <div className="form-wrap__col fixed-height-220 width--full">
                            <div className="change-issuer-request__group">
                              <div className="form-group width--full mbpx-0">
                                <DebounceFormDropdown
                                  isMandatory
                                  name="newLeaveIssuerId"
                                  label="Change leave issuer to"
                                  type={LIST_TYPE.EMPLOYEE}
                                  debounceOptions={employeeDebounceOptions}
                                  value={props.values.newLeaveIssuerId}
                                  error={
                                    props.touched.newLeaveIssuerId
                                      ? getFormErrorText(props.errors.newLeaveIssuerId)
                                      : undefined
                                  }
                                  handleChange={handleChange}
                                  handleBlur={props.handleBlur}
                                  iconComponent={(option) => (
                                    <AvatarComponent
                                      label={String(option.label ?? '')}
                                      value={option as { empId?: string | number }}
                                    />
                                  )}
                                  isDisabled={isSubmitting || !isLeaveIssuerRequestEmpty || !canRequestLeaveIssuer}
                                  className="change-issuer-request__dropdown"
                                  classNamePrefix="change-issuer-request"
                                  customSearchText="Type to search and hit enter to select"
                                />

                                {canShowNewRequestLeaveIssuerInfo && (
                                  <Info
                                    message={getLeaveIssueCountMessage({
                                      issuerName: selectedLeaveIssuerName,
                                      issueCount: leaveIssueCount || 0,
                                      isLoggedInUser: formValues.newLeaveIssuerId === currentUserId,
                                    })}
                                    shouldAlert={(leaveIssueCount || 0) >= DEFAULT_ISSUE_ALERT_COUNT}
                                  />
                                )}

                                {canShowExistingRequestLeaveIssuerInfo && (
                                  <Info
                                    message={getLeaveIssueCountMessage({
                                      issuerName: getFirstName(leaveIssuerRequest.newLeaveIssuer.fullname),
                                      issueCount: leaveIssueCount || 0,
                                      isLoggedInUser:
                                        (formValues.newLeaveIssuerId || INITIAL_VALUES.newLeaveIssuerId) ===
                                        currentUserId,
                                    })}
                                    shouldAlert={(leaveIssueCount || 0) >= DEFAULT_ISSUE_ALERT_COUNT}
                                  />
                                )}
                              </div>

                              <div className="form-group width--full mbpx-0">
                                <InputWrapper
                                  label="Transition date"
                                  error={
                                    props.touched.transitionDate && typeof props.errors.transitionDate === 'string'
                                      ? props.errors.transitionDate
                                      : undefined
                                  }
                                  isMandatory
                                >
                                  <DatePicker
                                    date={props.values.transitionDate}
                                    dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                                    displayFormat="LL"
                                    hasError={Boolean(props.touched.transitionDate && props.errors.transitionDate)}
                                    isOutsideRange={(date) => date.isBefore(latestTransitionDate)}
                                    onDateChange={(selectedDate) => {
                                      props.setFieldTouched('transitionDate', true);
                                      props.setFieldValue('transitionDate', selectedDate);
                                    }}
                                    openDirection="down"
                                    placeholderText="Pick a Transition Date"
                                    showMonthDropdown={true}
                                    showYearDropdown={true}
                                    customClassName="change-issuer-request__date-picker"
                                    disabled={isSubmitting || !isLeaveIssuerRequestEmpty}
                                  />
                                </InputWrapper>
                              </div>
                            </div>

                            <FormRadioGroup
                              onChange={(e) => {
                                props.handleChange(e);
                                if (e.target.value !== ROLE.OTHER) {
                                  props.setFieldValue('otherRole', '');
                                }
                              }}
                              onBlur={props.handleBlur}
                              value={props.values.newLeaveIssuerRole}
                              error={
                                props.touched.newLeaveIssuerRole
                                  ? getFormErrorText(props.errors.newLeaveIssuerRole as string | string[] | undefined)
                                  : undefined
                              }
                              label="New leave issuer is"
                              name="newLeaveIssuerRole"
                              disabled={isSubmitting || !isLeaveIssuerRequestEmpty}
                              radioList={NEW_LEAVE_ISSUER_IS_OPTIONS}
                              isMandatory
                            />
                            <Input
                              name="otherRole"
                              onBlur={props.handleBlur}
                              className="wp-25"
                              value={props.values.otherRole}
                              error={
                                props.touched.otherRole
                                  ? getFormErrorText(props.errors.otherRole as string | string[] | undefined)
                                  : undefined
                              }
                              onChange={props.handleChange}
                              disabled={
                                props.values.newLeaveIssuerRole !== ROLE.OTHER ||
                                isSubmitting ||
                                !isLeaveIssuerRequestEmpty
                              }
                              multiline={false}
                              type="text"
                            />
                            <div className="form-group">
                              <InputWrapper
                                label="Reason for changing leave issuer"
                                error={props.touched.reason ? getFormErrorText(props.errors.reason) : undefined}
                                isMandatory
                              >
                                <textarea
                                  name="reason"
                                  placeholder="Type your reason..."
                                  className={classNames('form-elem font-weight-regular change-issuer-request__reason', {
                                    'form-elem--error': props.touched.reason && props.errors.reason,
                                  })}
                                  value={props.values.reason}
                                  onChange={props.handleChange}
                                  onBlur={props.handleBlur}
                                  disabled={isSubmitting || !isLeaveIssuerRequestEmpty}
                                />
                              </InputWrapper>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex action-bar-footer ptpx-0">
                        <button
                          className={classNames(
                            'mrpx-15 btn font-weight-bold font-size-14 change-issuer-request__button',
                            {
                              'btn--primary':
                                !isSubmitting &&
                                (isLeaveIssuerRequestEmpty || isSelfNewIssuer) &&
                                canRequestLeaveIssuer,
                              'btn--disabled':
                                isSubmitting ||
                                (!isSelfNewIssuer && !isLeaveIssuerRequestEmpty) ||
                                !canRequestLeaveIssuer,
                              'cursor-not-allowed': !canRequestLeaveIssuer,
                            }
                          )}
                          type="submit"
                          disabled={
                            isSubmitting || (!isSelfNewIssuer && !isLeaveIssuerRequestEmpty) || !canRequestLeaveIssuer
                          }
                        >
                          {isSubmitting && <Loading />}

                          <span className={classNames({ invisible: isSubmitting })}>
                            {isLeaveIssuerRequestEmpty
                              ? 'Send Request'
                              : isSelfNewIssuer
                                ? 'Confirm'
                                : 'Request Pending'}
                          </span>
                        </button>
                        {isLeaveIssuerRequestEmpty && (
                          <button
                            type="button"
                            className="btn btn--outlined-grey f-left card-button mr-10 change-issuer-request__button"
                            disabled={isSubmitting}
                            onClick={history.goBack}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </>
        </div>
        <LeaveIssuersHistory leaveIssuers={leaveIssuerHistory} isLoading={isLoadingHistory} />
      </div>
    </main>
  );
};

export default ChangeIssuerRequest;
